export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  categorySlug: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
  image: string;
}

export interface Category {
  slug: string;
  name: string;
  description: string;
  icon: string;
}

export const categories: Category[] = [
  {
    slug: 'guide',
    name: '使用指南',
    description: '从零开始掌握18c.mic的核心功能与操作技巧，快速上手平台',
    icon: 'ri-book-open-line',
  },
  {
    slug: 'feature',
    name: '功能解析',
    description: '深入探索18c.mic的各项功能特性，了解平台全部潜力',
    icon: 'ri-apps-2-line',
  },
  {
    slug: 'case',
    name: '应用案例',
    description: '真实场景下的18c.mic应用实践，看用户如何发挥平台价值',
    icon: 'ri-lightbulb-line',
  },
  {
    slug: 'news',
    name: '最新资讯',
    description: '18c.mic平台更新动态、行业趋势与前沿技术分享',
    icon: 'ri-newspaper-line',
  },
  {
    slug: 'review',
    name: '深度评测',
    description: '专业评测视角，全面解读18c.mic的优势与适用场景',
    icon: 'ri-star-line',
  },
];

export const articles: Article[] = [
  {
    slug: 'quick-start-guide',
    title: '18c.mic 快速入门指南：5分钟完成平台配置',
    excerpt: '本文将带您快速完成18c.mic的初始配置，从注册账号到创建第一个项目，全程只需5分钟。适合所有新用户阅读。',
    content: `<h2>注册与登录</h2><p>访问18c.mic官网，点击右上角的「开始使用」按钮进入注册页面。您可以使用邮箱或手机号完成注册，整个过程仅需1分钟。</p><p>注册成功后，系统会自动引导您完成个人资料设置和偏好配置。</p><h2>创建第一个项目</h2><p>登录后进入控制台，点击「新建项目」按钮。18c.mic提供了多种项目模板供您选择：</p><ul><li>基础模板：适合快速启动</li><li>专业模板：包含完整功能配置</li><li>自定义模板：完全按需搭建</li></ul><p>选择适合的模板后，系统将在几秒内完成项目初始化。</p><h2>核心功能概览</h2><p>项目创建完成后，您将看到18c.mic的主操作界面。左侧导航栏包含以下核心模块：</p><ul><li>资源管理：上传、组织和检索各类资源</li><li>协作空间：邀请团队成员共同编辑</li><li>数据分析：查看项目使用数据和统计</li><li>设置中心：自定义项目参数与权限</li></ul><h2>发布与分享</h2><p>完成内容编辑后，点击「发布」按钮即可将项目上线。18c.mic支持自定义域名绑定和访问权限控制，确保发布过程安全可控。</p><p>以上就是18c.mic的快速入门流程。如需更深入的功能学习，请参考我们的其他指南文章。</p>`,
    category: '使用指南',
    categorySlug: 'guide',
    author: '18c.mic 团队',
    date: '2026-07-28',
    readTime: '5分钟',
    tags: ['入门教程', '快速上手', '新手指南'],
    image: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20laptop%20showing%20dashboard%20interface%2C%20clean%20white%20desk%2C%20warm%20natural%20lighting%2C%20soft%20shadows%2C%20professional%20tech%20environment%2C%20editorial%20style%20photography%2C%20harmonious%20composition&width=800&height=450&seq=article-qs-01&orientation=landscape',
  },
  {
    slug: 'platform-features-overview',
    title: '18c.mic 功能全景解析：从资源管理到智能协作',
    excerpt: '全面梳理18c.mic平台的各项功能模块，帮助您了解平台能够解决哪些实际问题，适用于怎样的工作场景。',
    content: `<h2>资源管理中心</h2><p>18c.mic的资源管理模块是整个平台的核心之一。它支持多种文件格式的上传、分类、标签化管理，并提供强大的全文搜索功能，让您在海量资源中快速定位所需内容。</p><p>智能分类算法能自动识别资源类型并推荐合适的分类标签，大幅减少手动操作时间。</p><h2>智能协作空间</h2><p>多人协作是18c.mic的一大亮点功能。您可以创建团队空间，邀请成员加入，实时查看编辑进度。所有操作记录均可追溯，确保团队协作透明高效。</p><p>支持评论、批注和任务分配功能，让远程协作也能井然有序。</p><h2>数据洞察面板</h2><p>18c.mic内置了强大的数据分析引擎，自动收集项目运行数据并以可视化图表呈现。您可以直观地了解资源使用频率、用户行为趋势和协作效率指标。</p><h2>安全与权限控制</h2><p>平台提供了细粒度的权限管理系统，支持角色分配、操作审计和访问日志。所有数据传输均采用加密协议，确保信息安全。</p>`,
    category: '功能解析',
    categorySlug: 'feature',
    author: '18c.mic 产品团队',
    date: '2026-07-25',
    readTime: '8分钟',
    tags: ['功能详解', '资源管理', '协作工具'],
    image: 'https://readdy.ai/api/search-image?query=Abstract%20geometric%20interface%20with%20interconnected%20nodes%2C%20clean%20white%20background%2C%20soft%20green%20accent%20highlights%2C%20modern%20tech%20aesthetic%2C%20minimal%20design%2C%20professional%20technology%20visualization&width=800&height=450&seq=article-feat-01&orientation=landscape',
  },
  {
    slug: 'enterprise-case-study',
    title: '某科技企业借助18c.mic实现研发效率提升300%',
    excerpt: '了解一家中型科技公司如何通过18c.mic平台重构内部资源管理体系，在6个月内将团队协作效率提升3倍的实战案例。',
    content: `<h2>客户背景</h2><p>某科技公司拥有200余名员工，分布在三个城市办公。在引入18c.mic之前，团队面临着资源分散、协作低效、知识沉淀困难等问题。</p><h2>面临挑战</h2><p>该公司的主要痛点包括：</p><ul><li>项目文档散落在不同平台，查找困难</li><li>跨地域团队沟通成本高昂</li><li>新员工上手周期长达2个月</li><li>知识资产无法有效积累和复用</li></ul><h2>解决方案</h2><p>经过全面评估，该企业选择18c.mic作为统一协作平台。部署方案包括：</p><ul><li>建立公司级资源库，统一管理所有项目文档</li><li>按部门创建协作空间，实现权限分级管理</li><li>利用智能标签系统，建立知识图谱</li><li>通过数据分析面板，持续优化工作流程</li></ul><h2>实施成果</h2><p>上线6个月后，该企业取得了显著成效：</p><ul><li>团队协作效率提升300%</li><li>新员工上手周期缩短至2周</li><li>文档查找时间减少80%</li><li>跨地域项目交付准时率提升至95%</li></ul><p>这个案例充分证明了18c.mic在企业级场景中的实际价值。</p>`,
    category: '应用案例',
    categorySlug: 'case',
    author: '18c.mic 客户成功团队',
    date: '2026-07-20',
    readTime: '6分钟',
    tags: ['企业案例', '效率提升', '最佳实践'],
    image: 'https://readdy.ai/api/search-image?query=Modern%20office%20environment%20with%20diverse%20team%20collaborating%20around%20digital%20displays%2C%20bright%20natural%20light%2C%20clean%20aesthetic%2C%20professional%20workspace%2C%20warm%20atmosphere%2C%20editorial%20photography&width=800&height=450&seq=article-case-01&orientation=landscape',
  },
  {
    slug: 'platform-update-v3',
    title: '18c.mic V3.0 重磅发布：智能推荐与自动化工作流上线',
    excerpt: '18c.mic迎来重大版本更新，全新智能推荐引擎和自动化工作流功能正式面向所有用户开放，带来前所未有的使用体验。',
    content: `<h2>版本亮点</h2><p>18c.mic V3.0 是平台迄今为止最重要的更新，我们引入了多项革命性功能。</p><h2>智能推荐引擎</h2><p>基于用户行为分析和机器学习算法，全新的智能推荐引擎能够自动为您推荐相关资源、协作者和项目模板。推荐准确率达到92%，随着使用时间的增加，推荐会越来越精准。</p><h2>自动化工作流</h2><p>可视化工作流编辑器让您无需编程即可创建自动化流程。支持触发条件设置、条件分支管理和多步骤操作编排，将重复性操作效率提升10倍以上。</p><h2>界面全新升级</h2><p>我们重新设计了用户界面，采用更简洁的布局和更直观的交互方式。新版本的学习曲线更平缓，新用户可在10分钟内上手。</p><h2>性能优化</h2><p>底层架构全面优化，页面加载速度提升40%，大文件处理能力增强3倍，为未来的功能扩展奠定了坚实基础。</p>`,
    category: '最新资讯',
    categorySlug: 'news',
    author: '18c.mic 开发团队',
    date: '2026-08-01',
    readTime: '4分钟',
    tags: ['版本更新', '新功能', 'V3.0'],
    image: 'https://readdy.ai/api/search-image?query=Futuristic%20digital%20interface%20with%20glowing%20nodes%20and%20data%20streams%2C%20dark%20background%20with%20green%20accents%2C%20modern%20technology%20visualization%2C%20clean%20minimal%20design%2C%20abstract%20data%20pattern&width=800&height=450&seq=article-news-01&orientation=landscape',
  },
  {
    slug: 'in-depth-review',
    title: '18c.mic 深度评测：为什么它是2026年最值得关注的平台',
    excerpt: '从功能、性能、易用性、生态四个维度对18c.mic进行全面评测，客观分析其优势与不足，帮助您做出选择决策。',
    content: `<h2>评测概述</h2><p>本次评测基于18c.mic V3.0版本，从功能完整度、性能表现、用户体验和生态建设四个维度进行系统评估。</p><h2>功能完整度：9.2/10</h2><p>18c.mic的功能覆盖范围非常全面，从基础的资源管理到高级的自动化工作流，几乎满足了中小型团队的所有需求。智能推荐和数据分析功能是突出的加分项。</p><h2>性能表现：8.8/10</h2><p>在模拟100人并发操作的压测中，平台响应时间保持在200ms以内。大文件上传和处理的效率令人满意，但批量操作时偶有轻微延迟。</p><h2>用户体验：9.5/10</h2><p>界面设计简洁优雅，操作逻辑清晰一致。新用户引导做得非常出色，学习曲线平缓。移动端适配也相当到位。</p><h2>生态建设：8.5/10</h2><p>API接口文档完善，第三方集成支持覆盖主流工具。社区活跃度持续增长，官方教程资源丰富。但部分高级集成的配置流程仍有简化空间。</p><h2>总结</h2><p>综合来看，18c.mic是2026年最值得关注的平台之一。它在功能完整度和用户体验方面表现尤其出色，适合各类规模的团队使用。</p>`,
    category: '深度评测',
    categorySlug: 'review',
    author: '18c.mic 评测团队',
    date: '2026-07-15',
    readTime: '10分钟',
    tags: ['深度评测', '平台对比', '选型指南'],
    image: 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20desk%20setup%20with%20sleek%20laptop%20displaying%20analytics%20dashboard%2C%20warm%20ambient%20lighting%2C%20professional%20workspace%2C%20editorial%20product%20photography%2C%20soft%20shadows%2C%20harmonious%20color%20palette&width=800&height=450&seq=article-review-01&orientation=landscape',
  },
  {
    slug: 'collaboration-best-practices',
    title: '18c.mic 团队协作最佳实践：高效远程办公指南',
    excerpt: '总结18c.mic平台上团队协作的六大最佳实践，帮助分布式团队打造流畅高效的远程工作体验。',
    content: `<h2>引言</h2><p>远程办公已成为现代企业的常态，如何利用18c.mic实现高效远程协作是每个团队管理者关心的话题。</p><h2>实践一：建立清晰的空间结构</h2><p>在18c.mic中，合理的空间层级设计是高效协作的基础。建议按「公司-部门-项目-模块」四级结构创建协作空间，确保每位成员都能快速找到自己需要的内容。</p><h2>实践二：善用标签系统</h2><p>18c.mic的标签系统不仅仅是分类工具，更是知识管理的核心。建议建立统一的标签命名规范，并定期清理冗余标签。</p><h2>实践三：设置自动化提醒</h2><p>利用自动化工作流功能，为关键节点设置自动提醒。如文档待审、任务到期、评论回复等场景，减少人工跟进成本。</p><h2>实践四：定期数据回顾</h2><p>每周查看数据洞察面板，关注协作活跃度、瓶颈环节和资源利用率三个核心指标，持续优化协作流程。</p><h2>实践五：建立知识库</h2><p>将常见问题、操作手册和项目复盘沉淀为知识库文章，降低重复沟通成本。</p><h2>实践六：制定使用规范</h2><p>团队内部应明确18c.mic的使用规范，包括文件命名、权限申请流程和空间管理规则，确保平台使用的一致性。</p>`,
    category: '使用指南',
    categorySlug: 'guide',
    author: '18c.mic 内容团队',
    date: '2026-07-10',
    readTime: '7分钟',
    tags: ['团队协作', '远程办公', '最佳实践'],
    image: 'https://readdy.ai/api/search-image?query=Diverse%20remote%20team%20collaborating%20through%20digital%20screens%2C%20modern%20home%20office%20setting%2C%20warm%20lighting%2C%20clean%20minimal%20design%2C%20professional%20editorial%20style%2C%20soft%20natural%20light&width=800&height=450&seq=article-guide-02&orientation=landscape',
  },
  {
    slug: 'data-driven-decisions',
    title: '用18c.mic数据洞察做出更明智的业务决策',
    excerpt: '如何利用18c.mic强大的数据分析功能，将原始使用数据转化为有价值的业务洞察，驱动团队做出更好的决策。',
    content: `<h2>数据驱动的价值</h2><p>在当今的商业环境中，数据驱动决策已成为企业竞争力的核心。18c.mic的数据洞察面板提供了全方位的数据可视化能力。</p><h2>关键指标解读</h2><p>18c.mic的数据面板覆盖了多个维度的核心指标：</p><ul><li>活跃度指标：DAU、MAU、会话时长</li><li>协作效率：任务完成率、平均响应时间</li><li>资源利用：存储使用率、热门资源排行</li><li>质量指标：错误率、用户满意度评分</li></ul><h2>制定数据驱动策略</h2><p>建议从以下步骤开始：首先确定业务目标，然后选择对应指标，设定基线和目标值，定期复盘并调整策略。</p><h2>实际案例</h2><p>某团队通过数据分析发现，文档审核环节是协作瓶颈，平均耗时48小时。通过优化审核流程并在18c.mic中设置自动化提醒，将审核时间缩短至4小时。</p>`,
    category: '功能解析',
    categorySlug: 'feature',
    author: '18c.mic 数据团队',
    date: '2026-07-05',
    readTime: '6分钟',
    tags: ['数据分析', '决策支持', '数据洞察'],
    image: 'https://readdy.ai/api/search-image?query=Abstract%20data%20visualization%20with%20charts%20and%20graphs%20on%20dark%20background%2C%20green%20accent%20colors%2C%20minimal%20modern%20design%2C%20professional%20analytics%20interface%2C%20clean%20geometric%20patterns&width=800&height=450&seq=article-feat-02&orientation=landscape',
  },
  {
    slug: 'startup-growth-story',
    title: '创业团队从0到1：18c.mic陪伴式成长纪实',
    excerpt: '一支5人创业团队分享他们如何使用18c.mic从零搭建工作体系，在18个月内实现产品上线并获得A轮融资的完整经历。',
    content: `<h2>起点</h2><p>2025年初，我们的创业团队只有5个人，办公地点是租用的一间小会议室。面对产品研发、市场推广、客户沟通等多线任务，我们急需一个能够串联所有工作的平台。</p><h2>选择18c.mic</h2><p>在对比了多个协作工具后，我们选择了18c.mic。原因很简单：它在功能全面性和易用性之间找到了最佳平衡点，而且学习成本极低。</p><h2>构建工作体系</h2><p>我们用18c.mic建立了从需求管理到产品交付的完整工作流。研发文档、设计稿、市场素材全部统一管理，团队每个人都能随时获取所需信息。</p><h2>关键转折</h2><p>产品上线第3个月，我们迎来了第一个大型客户。借助18c.mic的协作能力，团队高效完成了定制化需求交付，赢得了客户信任和口碑传播。</p><h2>融资之路</h2><p>准备融资材料时，18c.mic的数据洞察面板帮了大忙。我们能够快速整理出产品使用数据、团队协作效率和项目交付记录，让投资人直观了解团队的运营能力。</p><h2>现在与未来</h2><p>18个月后的今天，团队已扩展至30人，A轮融资顺利完成。18c.mic始终是我们最依赖的协作平台。</p>`,
    category: '应用案例',
    categorySlug: 'case',
    author: '18c.mic 社区',
    date: '2026-06-28',
    readTime: '8分钟',
    tags: ['创业故事', '从0到1', '用户分享'],
    image: 'https://readdy.ai/api/search-image?query=Small%20startup%20team%20brainstorming%20in%20cozy%20modern%20office%2C%20warm%20natural%20light%2C%20clean%20white%20aesthetic%2C%20plant%20decor%2C%20creative%20atmosphere%2C%20editorial%20photography%2C%20soft%20harmonious%20tones&width=800&height=450&seq=article-case-02&orientation=landscape',
  },
  {
    slug: 'security-architecture',
    title: '18c.mic 安全体系架构白皮书解读',
    excerpt: '深入了解18c.mic平台的安全架构设计，包括数据加密、访问控制、审计日志和合规认证等方面。',
    content: `<h2>安全理念</h2><p>18c.mic将安全视为产品的生命线。平台安全体系遵循「纵深防御」原则，在数据传输、存储、访问和审计四个层面构建了完整的安全防护体系。</p><h2>数据加密</h2><p>传输层使用TLS 1.3协议，存储层采用AES-256加密算法。用户敏感数据额外增加应用层加密，即使数据库被非法访问也无法解密。</p><h2>访问控制</h2><p>支持RBAC（基于角色的访问控制）和ABAC（基于属性的访问控制）双模式，可精确控制到单个资源的操作权限。</p><h2>审计日志</h2><p>所有操作均有完整审计记录，支持实时监控和异常行为告警。日志数据采用只追加写入模式，确保不可篡改。</p><h2>合规认证</h2><p>18c.mic已通过SOC 2 Type II、ISO 27001等国际安全认证，符合GDPR和《个人信息保护法》的要求。</p>`,
    category: '最新资讯',
    categorySlug: 'news',
    author: '18c.mic 安全团队',
    date: '2026-06-20',
    readTime: '5分钟',
    tags: ['安全架构', '数据保护', '合规认证'],
    image: 'https://readdy.ai/api/search-image?query=Abstract%20digital%20security%20concept%20with%20shield%20shapes%20and%20encrypted%20data%20patterns%2C%20dark%20background%20with%20green%20glowing%20elements%2C%20modern%20cybersecurity%20visualization%2C%20minimal%20clean%20design&width=800&height=450&seq=article-news-02&orientation=landscape',
  },
  {
    slug: 'platform-comparison-2026',
    title: '2026年协作平台横向对比：18c.mic vs 竞品全面分析',
    excerpt: '从价格、功能、易用性、生态四个维度，将18c.mic与主流协作平台进行全面对比，帮您做出理性选择。',
    content: `<h2>对比范围</h2><p>本次对比选取了市场上最主流的5款协作平台，从客观数据出发进行横向评估。</p><h2>价格对比</h2><p>18c.mic的定价处于中等水平，但免费版功能限制最少，性价比突出。付费版按团队规模计费，中小企业友好度高。</p><h2>功能对比</h2><p>在核心功能完整度上，18c.mic与头部产品持平。在智能推荐和自动化工作流方面具有明显优势。但在第三方集成数量上尚有提升空间。</p><h2>易用性对比</h2><p>18c.mic的用户体验评分在同类产品中排名前二。界面设计简洁直观，新用户引导完善，学习成本最低。</p><h2>总结建议</h2><p>对于追求高性价比和优秀用户体验的团队，18c.mic是当前最佳选择。如果对特定第三方集成有刚需，建议先确认兼容性。</p>`,
    category: '深度评测',
    categorySlug: 'review',
    author: '18c.mic 市场团队',
    date: '2026-06-15',
    readTime: '9分钟',
    tags: ['竞品对比', '选型建议', '平台评测'],
    image: 'https://readdy.ai/api/search-image?query=Modern%20comparison%20grid%20interface%20design%2C%20clean%20white%20background%2C%20minimal%20layout%2C%20professional%20product%20photography%2C%20soft%20green%20accent%20touches%2C%20editorial%20style&width=800&height=450&seq=article-review-02&orientation=landscape',
  },
];